import type { AxiosRequestConfig } from 'axios';

import { SecondarySelections } from '@/models';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

import apiClient from '../api-client';

interface UserListParams {
  page?: number;
  limit?: number;
  sortDirection: 'asc' | 'desc';
  sortColumn: string;
  search: string;
  statusCode: number;
  userGroupId?: string;
  workFlowStatus?: string;
  isExport?: boolean;
}

export const getTenantUserList = async ({
  page,
  search,
  sortColumn,
  sortDirection,
  statusCode,
  isExport,
  limit,
  userGroupId,
  workFlowStatus,
}: UserListParams) => {
  const params: Record<string, any> = {};
  if (isExport) {
    params.isExport = true;
  } else {
    if (page !== undefined) params.offset = page;
    if (limit !== undefined) params.limit = limit;
  }
  if (search) params.searchField = search;
  if (sortColumn) params.sortBy = sortColumn;
  if (sortDirection) params.sortDir = sortDirection;
  if (statusCode) params.statusId = statusCode;
  if (userGroupId) params.userGroupId = userGroupId;
  if (workFlowStatus) params.workFlowStatus = workFlowStatus;

  const queryString = new URLSearchParams(params).toString();
  const url = `${API_ENDPOINTS?.TENANT}?${queryString}`;

  const res = await apiClient.get(url);
  return res?.data?.data;
};

export const searchTenantUser = async (search: string) => {
  const params: Record<string, any> = {};
  if (search) params.searchField = search;

  const queryString = new URLSearchParams(params).toString();
  const url = `${API_ENDPOINTS?.TENANT}?${queryString}`;

  const res = await apiClient.get(url);
  return res?.data?.data;
};

export const fetchTenantUserById = async (userId: string) => {
  const res = await apiClient.get(`${API_ENDPOINTS?.TENANT}/${userId}`);
  return res?.data?.data;
};

/**
 * The signed-in user's own details, for My Profile. Unlike GET user/{id},
 * which needs User.View, this needs no User Management permission: the
 * backend takes the id from the token, so it can only return the caller.
 */
export const fetchMyProfile = async () => {
  // The page reports a failure itself: the interceptor stays silent on a 403,
  // and toasting other errors here as well would show the message twice.
  const config: AxiosRequestConfig & { skipErrorToast?: boolean } = {
    skipErrorToast: true,
  };
  const res = await apiClient.get(`${API_ENDPOINTS?.TENANT}/profile`, config);
  return res?.data?.data;
};
export const createTenantUser = async (payload: any) => {
  const res = await apiClient.post(`${API_ENDPOINTS?.TENANT}`, payload);
  return res?.data;
};

export const updateTenantUser = async (payload: any, userId: string) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.TENANT}/${userId}`,
    payload
  );
  return res?.data;
};

export const deleteTenantUser = async (userId: string) => {
  const res = await apiClient.delete(`${API_ENDPOINTS?.TENANT}/${userId}`);
  return res?.data;
};

export const updateTenantUserStatus = async (userId: string, payload: any) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.TENANT}/status/${userId}`,
    payload
  );
  return res?.data;
};

export const fetchUserManagementApprovalList = async (params: {
  type: string;
  offset?: number;
  limit?: number;
  moduleName: string;
  // txnStatus: string;
  search?: string;
  workflowStatusId?: number | string | (number | string)[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
}) => {
  const {
    type,
    offset,
    limit,
    search,
    moduleName,
    workflowStatusId,
    sortBy,
    sortDir,
    isExport,
  } = params;
  // &txnStatus=${txnStatus}
  const searchParams = new URLSearchParams();
  searchParams.set('moduleName', moduleName);

  if (isExport) {
    searchParams.set('isExport', 'true');
  } else {
    if (offset !== undefined) searchParams.set('offset', String(offset));
    if (limit !== undefined) searchParams.set('limit', String(limit));
  }

  if (workflowStatusId !== undefined) {
    searchParams.set('workflowStatusId', String(workflowStatusId));
  }
  if (sortBy) searchParams.set('sortBy', sortBy);
  if (sortDir) searchParams.set('sortDir', sortDir);
  if (search) searchParams.set('searchField', search);

  const queryString = searchParams.toString();

  const res = await apiClient.get(
    `${API_ENDPOINTS?.MAINTENANCE}/${type}?${queryString}`
  );
  return res?.data?.data;
};

export const userDetailsForApproval = async (txnId: string) => {
  const res = await apiClient.get(`${API_ENDPOINTS?.MAINTENANCE}/${txnId}`);
  return res?.data?.data;
};

export const approveOrRejectUserOrUserGroup = async (payload: {
  txnId: string;
  action: string;
  remarks?: string;
  type: string;
}) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS?.[payload.type === 'users' ? 'TENANT' : 'UserGroup']}/checker-action`,
    payload
  );
  return res?.data;
};

export const approveOrRejectUserGroup = async (payload: {
  txnId: string;
  action: string;
  remarks?: string;
}) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS?.UserGroup}/checker-action`,
    payload
  );
  return res?.data;
};

// Users Groups:
export const fetchUserGroupsList = async ({
  page,
  limit,
  search,
  sortDirection,
  sortColumn,
  statusCode,
  isExport,
  userGroupId,
  dataAccessLevels,
  systemModules,
  secondarySelections,
}: {
  page?: number;
  limit?: number;
  sortDirection: 'asc' | 'desc';
  sortColumn: string;
  search: string;
  statusCode: number;
  isExport?: boolean;
  userGroupId?: string;
  dataAccessLevels?: number[];
  systemModules?: number[];
  secondarySelections?: SecondarySelections;
}) => {
  const params: Record<string, any> = {
    isExport: isExport ? true : undefined,
    offset: isExport ? undefined : page,
    limit: isExport ? undefined : limit,
    searchField: search || undefined,
    sortBy: sortColumn || undefined,
    sortDir: sortDirection || undefined,
    StatusId: statusCode || undefined,
    userGroupId: userGroupId || undefined,
    DataAccessLevelInfos: dataAccessLevels?.length
      ? dataAccessLevels
      : undefined,
    SystemModuleInfos: systemModules?.length ? systemModules : undefined,
  };

  const cleanedParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined)
  );

  const cleanedSelections = Object.fromEntries(
    Object.entries(secondarySelections || {}).filter(([_, v]) => v?.length > 0)
  );

  if (Object.keys(cleanedSelections).length > 0) {
    cleanedParams.SecondarySelections = JSON.stringify(cleanedSelections);
  }

  const queryString = new URLSearchParams(cleanedParams).toString();
  const res = await apiClient.get(`${API_ENDPOINTS?.UserGroup}?${queryString}`);
  return res?.data?.data;
};

export const fetchUserGroupById = async (groupId: number | string) => {
  const res = await apiClient.get(`${API_ENDPOINTS?.UserGroup}/${groupId}`);
  return res?.data?.data;
};

export const createUserGroup = async (payload: any) => {
  const res = await apiClient.post(`${API_ENDPOINTS?.UserGroup}`, payload);
  return res?.data;
};

export const updateUserGroup = async (payload: any, groupId: number) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.UserGroup}/${groupId}`,
    payload
  );
  return res?.data;
};

export const deleteUserGroup = async (groupId: number) => {
  const res = await apiClient.delete(`${API_ENDPOINTS?.UserGroup}/${groupId}`);
  return res?.data;
};

export const updateUserGroupStatus = async (
  groupId: number | string,
  payload: { statusId: number }
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.UserGroup}/status/${groupId}`,
    payload
  );
  return res?.data;
};

export const submitUserGroupRevision = async (txnId: string, payload: any) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.UserGroup}/revision/${txnId}`,
    payload
  );
  return res?.data;
};

export const submitUserRevision = async (userId: string, payload: any) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.TENANT}/revision/${userId}`,
    payload
  );
  return res?.data;
};

export const fetchStatusList = async () => {
  const res = await apiClient.get(`${API_ENDPOINTS?.MASTER}/status`);
  return res?.data?.data ?? [];
};

export const fetchEmployeeStatusList = async () => {
  const res = await apiClient.get(`${API_ENDPOINTS?.MASTER}/employee-status`);
  return res?.data?.data ?? [];
};

export const getMasterUserList = async () => {
  const res = await apiClient.get(`${API_ENDPOINTS.MASTER}/users`);
  return res?.data?.data || res?.data || [];
};
